import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Grid,
  IconButton,
  Typography,
  Box,
  Tooltip,
  Autocomplete,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr, enUS } from 'date-fns/locale';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import CakeIcon from '@mui/icons-material/Cake';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinkIcon from '@mui/icons-material/Link';
import { database } from '../../firebase';
import ownAuthSession from '../../services/auth/ownAuthSession';
import { ref, get, push, set, update } from 'firebase/database';
import firebaseDataService from '../../services/FirebaseDataService';
import { useTranslation } from 'react-i18next';

// Özel etkinlik tanımı
interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  createdAt: number;
  isChecked?: boolean;
  color?: string; // Özel etkinlik rengi
}

interface EventFormData {
  id?: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  repeatYearly: boolean;
  notes?: string;
  specialEventId?: string; // İlişkilendirilmiş özel etkinliğin ID'si
}

// Veritabanı için etkinlik veri tipi
interface EventDataForDB {
  id?: string;
  title: string;
  start: string; // ISO string
  end: string; // ISO string
  allDay: boolean;
  repeatYearly: boolean;
  notes?: string;
  specialEventId?: string;
  eventColor?: string; // Özel etkinlik rengi
  createdAt: number;
  createdBy: string;
  updatedAt: number;
}

interface EventFormModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (eventData: EventFormData) => void;
  onDelete?: () => void;
  initialData?: Partial<EventFormData>;
  isEditing?: boolean;
}

const EventFormModal: React.FC<EventFormModalProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  initialData,
  isEditing = false
}) => {
  const { t, i18n } = useTranslation('other');
  const adapterLocale = i18n.language?.startsWith('tr') ? tr : enUS;
  const [eventData, setEventData] = useState<EventFormData>({
    title: '',
    start: new Date(),
    end: new Date(new Date().setHours(new Date().getHours() + 1)),
    allDay: false,
    repeatYearly: false,
    notes: '',
    specialEventId: ''
  });

  const [errors, setErrors] = useState({
    title: false,
    start: false,
    end: false
  });
  
  // Özel etkinlikleri tutacak state
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [loadingCalendarEvents, setLoadingCalendarEvents] = useState(false);
  const [selectedCalendarEvent, setSelectedCalendarEvent] = useState<CalendarEvent | null>(null);
  
  // Kaydetme işlemi için loading state
  const [isSaving, setIsSaving] = useState(false);
  
  // Snackbar için state'ler
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  
  // Silme işlemi onay dialog state'i
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Özel etkinlikleri yükleme
  useEffect(() => {
    const loadCalendarEvents = async () => {
      if (!open) return; // Modal açık değilse yükleme
      
      try {
        setLoadingCalendarEvents(true);
        
        // Giriş yapmış kullanıcıyı kontrol et
        const user = ownAuthSession.getCurrentUid() ? { uid: ownAuthSession.getCurrentUid() as string } : null;
        if (!user) {
          setLoadingCalendarEvents(false);
          return;
        }
        
        // Kullanıcı verilerini al ve şirket ID'sini bul
        const userData = await firebaseDataService.getUserData(user.uid);
        if (!userData) {
          setLoadingCalendarEvents(false);
          return;
        }
        const companyId = userData.companyId || null;
        
        if (!companyId) {
          setLoadingCalendarEvents(false);
          return;
        }
        
        // Özel etkinlikleri yükle
        const calendarEventsRef = ref(database, `companies/${companyId}/events/calendarEvents`);
        const snapshot = await get(calendarEventsRef);
        
        if (!snapshot.exists()) {
          setCalendarEvents([]);
          setLoadingCalendarEvents(false);
          return;
        }
        
        const eventsData = snapshot.val();
        const eventsList = Object.entries(eventsData).map(([id, data]: [string, any]) => ({
          id,
          title: data.title || t('events.messages.untitledEvent'),
          description: data.description || '',
          createdAt: data.createdAt || Date.now(),
          isChecked: data.isChecked !== undefined ? data.isChecked : true,
          color: data.color || '#1976d2'
        }));
        
        // Oluşturma tarihine göre sırala (yeniden eskiye)
        eventsList.sort((a, b) => b.createdAt - a.createdAt);
        
        setCalendarEvents(eventsList);
        
        // Eğer etkinlik düzenleniyorsa ve specialEventId varsa seçili etkinliği ayarla
        if (initialData?.specialEventId) {
          const selectedEvent = eventsList.find(event => event.id === initialData.specialEventId);
          if (selectedEvent) {
            setSelectedCalendarEvent(selectedEvent);
          }
        }
      } catch (error) {
        console.error('Error while loading special events:', error);
      } finally {
        setLoadingCalendarEvents(false);
      }
    };
    
    loadCalendarEvents();
  }, [open, initialData, t]);

  // Form verilerini sıfırla
  useEffect(() => {
    if (open) {
      if (initialData) {
        setEventData({
          title: initialData.title || '',
          start: initialData.start || new Date(),
          end: initialData.end || new Date(new Date().setHours(new Date().getHours() + 1)),
          allDay: initialData.allDay || false,
          repeatYearly: initialData.repeatYearly || false,
          notes: initialData.notes || '',
          specialEventId: initialData.specialEventId || ''
        });
      } else {
        // Varsayılan değerler
        setEventData({
          title: '',
          start: new Date(),
          end: new Date(new Date().setHours(new Date().getHours() + 1)),
          allDay: false,
          repeatYearly: false,
          notes: '',
          specialEventId: ''
        });
      }
      setErrors({
        title: false,
        start: false,
        end: false
      });
    }
  }, [open, initialData]);

  // Form açıldığında/kapandığında state'leri sıfırla
  useEffect(() => {
    if (!open) {
      setIsSaving(false);
      setIsDeleting(false);
    }
  }, [open]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEventData({
      ...eventData,
      [name]: value
    });

    if (name === 'title') {
      setErrors({
        ...errors,
        title: value.trim() === ''
      });
    }
  };

  const handleDateChange = (date: Date | null, field: 'start' | 'end') => {
    if (date) {
      setEventData({
        ...eventData,
        [field]: date
      });

      // End tarihi start tarihinden önce olamaz kontrolü
      if (field === 'end' && eventData.start > date) {
        setErrors({
          ...errors,
          end: true
        });
      } else if (field === 'start' && date > eventData.end) {
        setErrors({
          ...errors,
          start: true
        });
      } else {
        setErrors({
          ...errors,
          start: false,
          end: false
        });
      }
    }
  };

  const handleAllDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const allDay = e.target.checked;
    let newStart = new Date(eventData.start);
    let newEnd = new Date(eventData.end);
    
    if (allDay) {
      // Tüm gün etkinliği için saatleri sıfırla
      newStart.setHours(0, 0, 0, 0);
      newEnd.setHours(23, 59, 59, 999);
    }
    
    setEventData({
      ...eventData,
      allDay,
      start: newStart,
      end: newEnd
    });
  };

  // Her yıl tekrarla seçeneği için handler fonksiyonu
  const handleRepeatYearlyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEventData({
      ...eventData,
      repeatYearly: e.target.checked
    });
  };

  const handleSubmit = async () => {
    // Form doğrulama
    const newErrors = {
      title: eventData.title.trim() === '',
      start: false,
      end: eventData.end < eventData.start
    };

    setErrors(newErrors);

    if (!newErrors.title && !newErrors.start && !newErrors.end) {
      setIsSaving(true);
      
      try {
        // Giriş yapmış kullanıcıyı kontrol et
        const user = ownAuthSession.getCurrentUid() ? { uid: ownAuthSession.getCurrentUid() as string } : null;
        if (!user) {
          throw new Error(t('events.messages.userLoginNotFound'));
        }
        
        // Kullanıcı verilerini al ve şirket ID'sini bul
        const userData = await firebaseDataService.getUserData(user.uid);
        if (!userData) {
          throw new Error(t('events.messages.userInfoNotFound'));
        }
        const companyId = userData.companyId || null;
        
        if (!companyId) {
          throw new Error(t('events.messages.companyInfoNotFound'));
        }
        
        // Etkinlik verilerini hazırla
        const eventDataForDB: EventDataForDB = {
          title: eventData.title,
          start: eventData.start.toISOString(),
          end: eventData.end.toISOString(),
          allDay: eventData.allDay,
          repeatYearly: eventData.repeatYearly,
          notes: eventData.notes || '',
          createdAt: Date.now(),
          createdBy: user.uid,
          updatedAt: Date.now()
        };
        
        // Özel etkinlik seçilmişse, ilişkilendirme ID'sini ekle
        if (eventData.specialEventId) {
          eventDataForDB.specialEventId = eventData.specialEventId;
          
          // Seçilen özel etkinliğin rengini bul ve ekle
          const selectedEvent = calendarEvents.find(event => event.id === eventData.specialEventId);
          if (selectedEvent && selectedEvent.color) {
            eventDataForDB.eventColor = selectedEvent.color;
          }
        }
        
        // Veritabanına kaydet
        let eventId = '';
        
        // Düzenleme mi, yeni kayıt mı kontrol et
        if (isEditing && initialData?.id) {
          // Mevcut etkinliği güncelle
          eventId = initialData.id as string;
          const eventRef = ref(database, `companies/${companyId}/events/${eventId}`);
          await update(eventRef, {
            ...eventDataForDB,
            updatedAt: Date.now()
          });
          
          setSnackbarMessage(t('events.messages.eventUpdated'));
        } else {
          // Yeni etkinlik oluştur
          const eventsRef = ref(database, `companies/${companyId}/events`);
          const newEventRef = push(eventsRef);
          eventId = newEventRef.key as string;
          
          await set(newEventRef, {
            ...eventDataForDB,
            id: eventId
          });
          
          setSnackbarMessage(t('events.messages.eventCreated'));
        }
        
        // ÇÖZÜM: onSave fonksiyonunu çağır - veritabanına kaydetme işlemi onSave içinde de gerçekleşebiliyor
        // bu nedenle bu fonksiyon içinde yapmayız, sadece ID ve gerekli bilgileri taşıyan eventData'yı döneriz
        const finalEventData = {
          ...eventData,
          id: eventId,
          _savedByModal: true, // Events.tsx'te çift kayıt yapmaması için işaret
          eventColor: eventDataForDB.eventColor // Özel etkinlik rengini ekleyelim
        };
        onSave(finalEventData);
        
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        
        // Modalı kapat
        onClose();
      } catch (error: any) {
        console.error('Error while saving event:', error);
        setSnackbarMessage(error.message || t('events.messages.eventSaveError'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setIsSaving(false);
      }
    }
  };

  // Silme işlemi için onay dialogu açma
  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
  };
  
  // Silme işlemini iptal etme
  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
  };
  
  // Silme işlemini onaylama
  const handleConfirmDelete = () => {
    if (onDelete) {
      setIsDeleting(true);
      onDelete();
      // Not: onDelete fonksiyonu genellikle bir Promise döndürmez
      // Bu yüzden isDeleting burada kalır, modal kapandığında reset olur
    }
    setDeleteConfirmOpen(false);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ 
          m: 0, 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: 'primary.main',
          color: 'white'
        }}>
          <Typography variant="h6">
            {isEditing ? t('events.eventForm.titleEdit') : t('events.eventForm.titleCreate')}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: 'white'
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                name="title"
                label={t('events.eventForm.fields.title')}
                fullWidth
                value={eventData.title}
                onChange={handleInputChange}
                error={errors.title}
                helperText={errors.title ? t('events.eventForm.validation.titleRequired') : ''}
                autoFocus
                required
                variant="outlined"
                sx={{ mb: 2 }}
              />
            </Grid>

            {/* Özel Etkinlik Seçimi Autocomplete */}
            <Grid item xs={12}>
              <Autocomplete
                options={calendarEvents}
                loading={loadingCalendarEvents}
                getOptionLabel={(option) => option.title}
                value={selectedCalendarEvent}
                onChange={(event, newValue) => {
                  setSelectedCalendarEvent(newValue);
                  setEventData({
                    ...eventData,
                    specialEventId: newValue?.id || ''
                  });
                }}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LinkIcon sx={{ color: 'primary.main', mr: 1, fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {option.title}
                        </Typography>
                        {option.description && (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {option.description.length > 50 
                              ? `${option.description.substring(0, 50)}...` 
                              : option.description}
                          </Typography>
                        )}
                      </Box>
                      {/* Renk göstergesi */}
                      {option.color && (
                        <Box 
                          sx={{ 
                            ml: 'auto', 
                            width: 16, 
                            height: 16, 
                            borderRadius: '50%', 
                            backgroundColor: option.color,
                            border: '1px solid rgba(0,0,0,0.1)'
                          }} 
                        />
                      )}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('events.eventForm.fields.specialEvent')}
                    variant="outlined"
                    helperText={t('events.eventForm.fields.specialEventHelper')}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCalendarEvents ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{ mb: 2 }}
              />
              
              {selectedCalendarEvent && (
                <Box
                  sx={{
                    p: 1.5,
                    mb: 2,
                    bgcolor: 'info.light',
                    color: 'info.contrastText',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    borderLeft: `6px solid ${selectedCalendarEvent.color || '#1976d2'}`
                  }}
                >
                  <LinkIcon sx={{ mr: 1, fontSize: 20 }} />
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {t('events.eventForm.linkedSpecialEvent', { title: selectedCalendarEvent.title })}
                    </Typography>
                    {selectedCalendarEvent.description && (
                      <Typography variant="caption">
                        {selectedCalendarEvent.description.length > 100 
                          ? `${selectedCalendarEvent.description.substring(0, 100)}...` 
                          : selectedCalendarEvent.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <DateTimePicker
                label={t('events.eventForm.fields.startTime')}
                value={eventData.start}
                onChange={(date) => handleDateChange(date, 'start')}
                format="dd MMM yyyy HH:mm"
                ampm={false}
                slotProps={{ 
                  textField: { 
                    fullWidth: true,
                    variant: 'outlined',
                    error: errors.start,
                    helperText: errors.start ? t('events.eventForm.validation.invalidStartTime') : ''
                  } 
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DateTimePicker
                label={t('events.eventForm.fields.endTime')}
                value={eventData.end}
                onChange={(date) => handleDateChange(date, 'end')}
                format="dd MMM yyyy HH:mm"
                ampm={false}
                slotProps={{ 
                  textField: { 
                    fullWidth: true,
                    variant: 'outlined',
                    error: errors.end,
                    helperText: errors.end ? t('events.eventForm.validation.endAfterStart') : ''
                  } 
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={eventData.allDay}
                    onChange={handleAllDayChange}
                    color="primary"
                  />
                }
                label={t('events.eventForm.fields.allDay')}
                sx={{ 
                  mt: 2,
                  display: 'flex',
                  height: '56px'
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={eventData.repeatYearly}
                    onChange={handleRepeatYearlyChange}
                    color="primary"
                  />
                }
                label={
                  <>
                    {t('events.eventForm.fields.repeatYearly')}
                    <Tooltip title={t('events.eventForm.fields.repeatYearlyHelp')}>
                      <InfoOutlinedIcon fontSize="small" sx={{ ml: 0.5 }} />
                    </Tooltip>
                  </>
                }
                sx={{ 
                  mt: 2,
                  display: 'flex',
                  height: '56px'
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="notes"
                label={t('events.eventForm.fields.notes')}
                fullWidth
                multiline
                rows={4}
                value={eventData.notes}
                onChange={handleInputChange}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          {isEditing && onDelete && (
            <Button
              startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
              onClick={handleDeleteClick}
              color="error"
              variant="outlined"
              disabled={isDeleting || isSaving}
            >
              {isDeleting ? t('events.eventForm.actions.deleting') : t('events.eventForm.actions.deleteEvent')}
            </Button>
          )}
          
          <Box sx={{ ml: 'auto' }}>
            <Button onClick={onClose} sx={{ mr: 1 }} disabled={isSaving}>
              {t('events.eventForm.actions.cancel')}
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              color="primary"
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={20} /> : null}
            >
              {isEditing ? (isSaving ? t('events.eventForm.actions.updating') : t('events.eventForm.actions.update')) : (isSaving ? t('events.eventForm.actions.saving') : t('events.eventForm.actions.save'))}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Bildirim Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Silme işlemi onay dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{
            bgcolor: 'error.main',
            color: 'white',
            px: 3,
            py: 1.5,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          {t('events.eventForm.deleteDialog.title')}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 3 }}>
          <DialogContentText id="alert-dialog-description">
            {t('events.eventForm.deleteDialog.message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCancelDelete} variant="outlined">
            {t('events.eventForm.actions.cancel')}
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
            disabled={isDeleting}
            autoFocus
          >
            {isDeleting ? t('events.eventForm.actions.deleting') : t('events.eventForm.actions.deleteEvent')}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default EventFormModal; 